import { PATTERN_STATS } from './patternStats';
import { CURATED_PROBLEM_SPECS } from './curatedProblems';
import { ONLINE_PROBLEM_SPECS } from './onlineProblemSpecs';
import { BATCH_PROBLEM_SPECS } from './batchProblemSpecs';
import { API_QUESTION_SPECS } from './apiQuestionSpecs';
import { TEMPLATE_LIBRARY } from './templateLibrary';
import { inferVisualTypeFromPatterns } from './problemFactory';
import { PROBLEM_QUESTION_OVERRIDES } from './problemQuestionOverrides';
import { ONLINE_INTERACTIVE_TEMPLATE_BY_TITLE } from './onlineInteractiveMappings';

const slugify = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const pretty = (value) => JSON.stringify(value);

const escapeHtml = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/\"/g, '&quot;')
  .replace(/'/g, '&#39;');

const LONGEST_SUBSTRING_TITLE = 'Longest Substring Without Repeating Characters';

const describeArg = (value) => {
  if (Array.isArray(value)) {
    if (value.length === 0) return 'an empty array';
    if (Array.isArray(value[0])) return 'a 2D array';
    if (typeof value[0] === 'number') return 'an integer array';
    if (typeof value[0] === 'string') return 'a string array';
    return 'an array';
  }
  if (typeof value === 'string') return 'a string';
  if (typeof value === 'number') return 'an integer';
  if (typeof value === 'boolean') return 'a boolean value';
  if (value && typeof value === 'object') return 'an object';
  return 'an input value';
};

const buildInputSummary = (template) => {
  const firstTest = template.tests?.[0];
  const args = firstTest?.input || [];
  if (!Array.isArray(args) || args.length === 0) return 'You are given the required input.';
  if (args.length === 1) return `You are given ${describeArg(args[0])}.`;
  if (args.length === 2) {
    return `You are given ${describeArg(args[0])} and ${describeArg(args[1])}.`;
  }
  const parts = args.map((arg, idx) => `argument ${idx + 1} as ${describeArg(arg)}`);
  return `You are given ${parts.join(', ')}.`;
};

const buildMetadataStatement = (item) => `
<p><strong>${escapeHtml(item.title)}</strong></p>
<p><strong>Topic:</strong> ${escapeHtml((item.patterns || []).join(', ') || 'General')}</p>
<p><strong>Difficulty:</strong> ${escapeHtml(item.difficulty)}</p>
<p>This catalog entry was imported from an external source. The official problem statement should be used for exact input, output, and edge-case rules.</p>
`.trim();

const buildMetadataDescription = (item) => `
<p>This problem is listed to expand the catalog without inventing problem-specific content.</p>
<p>To avoid incorrect overlaps such as unrelated descriptions, examples, or solutions, in-app runner and visualizer content is only shown for problems with verified mappings.</p>
<p><strong>What you can rely on here:</strong> title, topic, and difficulty metadata for <strong>${escapeHtml(item.title)}</strong>.</p>
${item.sourceUrl ? `<p><strong>Official source:</strong> <a href="${escapeHtml(item.sourceUrl)}" target="_blank" rel="noreferrer">Open original problem statement</a></p>` : ''}
`.trim();

const buildMetadataConstraints = (item) => [
  `Review the official statement for exact constraints of ${item.title}.`,
  `Topic classification: ${(item.patterns || []).join(', ') || 'General'}.`,
  `Difficulty classification: ${item.difficulty}.`,
];

const buildDocxStatement = (item) => `
<p><strong>${escapeHtml(item.title)}</strong></p>
<p>${escapeHtml(item.statement || 'Problem statement is available in the source document.')}</p>
`.trim();

const buildDocxDescription = (item) => `
<p><strong>Input Format:</strong> ${escapeHtml(item.inputFormat || 'See source document.')}</p>
<p><strong>Output Format:</strong> ${escapeHtml(item.outputFormat || 'See source document.')}</p>
<p><strong>Expected Approach:</strong> ${escapeHtml(item.expectedApproach || 'Use an efficient approach suitable for constraints.')}</p>
`.trim();

const buildDocxConstraints = (item) => {
  const values = [];
  if (item.constraints) values.push(item.constraints);
  if (item.timeComplexity) values.push(`Time Complexity: ${item.timeComplexity}`);
  if (item.spaceComplexity) values.push(`Space Complexity: ${item.spaceComplexity}`);
  if (!values.length) values.push('Refer to source document for constraints.');
  return values;
};

const buildDocxExamples = (item) => {
  if (!item.sampleInput && !item.sampleOutput) return [];
  return [{
    title: 'Example 1',
    input: item.sampleInput || 'Not provided',
    output: item.sampleOutput || 'Not provided',
    explanation: item.explanation || 'See statement and constraints for expected behavior.',
  }];
};

const buildExampleReasoning = (template, test) => {
  const args = Array.isArray(test?.input) ? test.input : [];
  const out = pretty(test?.expected);
  const fn = template.functionName;

  if (fn === 'dailyTemperatures' && Array.isArray(args[0])) {
    return `Use a stack of day indices where we still have not found a warmer future day. Move left to right: when today's temperature is higher than the temperature at the stack top, pop that day and write the distance. After finishing the scan, unresolved days remain 0. This process gives ${out}.`;
  }

  if (fn === 'addTwoNumbers' && Array.isArray(args[0]) && Array.isArray(args[1])) {
    return `Add digits from both arrays position by position, just like manual addition. Keep a carry value, write digit (sum % 10), and pass carry (Math.floor(sum / 10)) to the next position. Continue until both arrays and carry are fully used. The resulting reverse-order digits are ${out}.`;
  }

  if (fn === 'findMin' && Array.isArray(args[0])) {
    return `Because the array is rotated but still made from sorted parts, compare mid with right boundary. If nums[mid] <= nums[right], minimum is in left half including mid; otherwise minimum is in right half. Repeating this leaves one index, whose value is ${out}.`;
  }

  if (fn === 'threeSumClosest' && Array.isArray(args[0]) && typeof args[1] === 'number') {
    return `We need three numbers whose sum is closest to target ${args[1]}. Try possible triplets after sorting the array and compare how far each sum is from the target. The best triplet gives sum ${out}, and no other checked triplet gets closer to ${args[1]}, so the correct output is ${out}.`;
  }

  if (fn === 'maxProfit' && Array.isArray(args[0])) {
    return `Read the prices from left to right. At each step, remember the cheapest price seen so far as the best buying day, then check how much profit you would get by selling today. The largest profit found during this scan is ${out}, so the output is ${out}.`;
  }

  if (fn === 'maxProfitSingle' && Array.isArray(args[0])) {
    return `Track two values while scanning once: minimum price seen so far and best profit so far. At each day, candidate profit is (todayPrice - minimumPrice). Update best if larger. This process yields ${out}, so the output is ${out}.`;
  }

  if (fn === 'twoSum' && Array.isArray(args[0]) && typeof args[1] === 'number') {
    const pair = Array.isArray(test.expected) ? test.expected : [];
    const nums = args[0];
    if (pair.length === 2 && nums[pair[0]] !== undefined && nums[pair[1]] !== undefined) {
      return `The target is ${args[1]}. At indices ${pair[0]} and ${pair[1]}, the values are ${nums[pair[0]]} and ${nums[pair[1]]}. Since ${nums[pair[0]]} + ${nums[pair[1]]} = ${args[1]}, the correct output is ${out}.`;
    }
    return `Find two positions whose values add up to target ${args[1]}. The returned indices are ${out}, so the correct output is ${out}.`;
  }

  if (fn === 'threeSum' && Array.isArray(args[0])) {
    return `We need all unique triplets whose sum is 0. After sorting, fix one number and use two pointers to find the other two numbers. Every triplet in ${out} adds up to 0, and duplicates are skipped, so ${out} is the correct answer.`;
  }

  if (fn === 'search' && Array.isArray(args[0])) {
    return `Because the array is sorted, we can check the middle value and remove half of the search space each time. This process eventually reaches the target position/result ${out}, so the output is ${out}.`;
  }

  if (fn === 'searchInsert' && Array.isArray(args[0]) && typeof args[1] === 'number') {
    return `Use binary search on the sorted array. If target ${args[1]} is found, return its index. If not found, when pointers cross, left points to the exact insertion position. For this input, that index is ${out}, so the output is ${out}.`;
  }

  if (fn === 'moveZeroesStable' && Array.isArray(args[0])) {
    return `Keep a write pointer for the next non-zero position. Scan with a read pointer; whenever read sees a non-zero value, swap it into the write position and advance write. Non-zero order is preserved and zeros shift to the end, giving ${out}.`;
  }

  if (fn === 'maxSubArrayKadane' && Array.isArray(args[0])) {
    return `At each index, choose the better option: start a new subarray at this value or extend the previous subarray sum. Maintain currentBestEndingHere and globalBest. Applying this over the full array gives maximum sum ${out}.`;
  }

  if (fn === 'merge' && Array.isArray(args[0])) {
    return `First sort the intervals by starting point. Then compare each interval with the last merged interval: if they overlap, combine them; otherwise keep them separate. After finishing this process, the merged intervals are ${out}, so the output is ${out}.`;
  }

  if (fn === 'climbStairs' && typeof args[0] === 'number') {
    return `To reach step ${args[0]}, the last move must come from either the previous step or two steps before. So the number of ways is built from smaller answers: ways(n) = ways(n-1) + ways(n-2). Following this pattern gives ${out}, so the output is ${out}.`;
  }

  if (fn === 'coinChange' && Array.isArray(args[0]) && typeof args[1] === 'number') {
    return `We build the answer for every amount from 0 up to ${args[1]}. For each amount, try every coin and keep the smallest number of coins that can make that amount. After filling these smaller answers, the minimum coins needed for ${args[1]} is ${out}, so the output is ${out}.`;
  }

  if (fn === 'canFinish') {
    return `Treat each course as a node and each prerequisite as a directed edge. If we can keep removing courses with no remaining prerequisites until all courses are processed, the answer is true; otherwise it is false. For this input, the result is ${out}, so the output is ${out}.`;
  }

  if (fn === 'numIslands') {
    return `Scan the grid cell by cell. Whenever an unvisited land cell is found, start a traversal to mark the whole connected island. The number of times this happens is ${out}, so the output is ${out}.`;
  }

  if (fn === 'lengthOfLongestSubstring' && typeof args[0] === 'string') {
    return `Move through the string while keeping a window with no repeated characters. If a repeated character appears, shift the left side of the window until all characters are unique again. The longest valid window has length ${out}, so the output is ${out}.`;
  }

  switch (template.visualType) {
    case 'array-pointer':
      return `Move the pointers according to the problem rule and evaluate the values they point to. After checking the needed candidates, the final correct answer is ${out}, so the output is ${out}.`;
    case 'binary-search':
      return `Use the middle value to decide which half can still contain the answer. Repeating this process narrows the search until the result becomes ${out}, so the output is ${out}.`;
    case 'stack':
      return `Process the input from left to right using stack push and pop operations. After all operations are applied, the final valid result is ${out}, so the output is ${out}.`;
    case 'window':
      return `Scan the input while maintaining a valid window or best running state. Update the answer whenever a better result is found. At the end, the computed result is ${out}, so the output is ${out}.`;
    case 'intervals':
      return `Sort the ranges, then combine overlapping ones step by step. After all overlaps are handled, the final answer becomes ${out}, so the output is ${out}.`;
    case 'dp':
      return `Solve smaller subproblems first, then use those answers to build the larger answer. After filling the DP states, the final result is ${out}, so the output is ${out}.`;
    case 'graph':
      return `Represent the input as a graph and apply traversal or ordering rules step by step. That process produces ${out}, so the output is ${out}.`;
    case 'linked-list':
      return `Move through the linked structure one node at a time and apply the required changes carefully. After processing all needed nodes, the final result is ${out}, so the output is ${out}.`;
    case 'queue':
      return `Follow the operations in order and update the queue state after each one. The collected final answer is ${out}, so the output is ${out}.`;
    case 'tree':
      return `Traverse the tree in the required order and compute the result from visited nodes. That produces ${out}, so the output is ${out}.`;
    default:
      return `Apply the given rule carefully to the sample input, and the final computed result is ${out}. Therefore, the correct output is ${out}.`;
  }
};

const buildBeginnerApproach = (template) => {
  const fn = template.functionName;

  if (fn === 'twoSum') {
    return [
      'Keep a hash map from value to index while scanning the array once.',
      'For each number x, compute needed = target - x and check if needed is already in the map.',
      'If found, return those two indices immediately; otherwise store x with its index and continue.',
    ];
  }

  if (fn === 'dailyTemperatures') {
    return [
      'Use a stack that stores indices of days in decreasing temperature order.',
      'When a warmer day appears, pop colder indices and fill their wait days as (todayIndex - poppedIndex).',
      'After the scan, indices left in stack have no warmer future day, so their answer stays 0.',
    ];
  }

  if (fn === 'addTwoNumbers') {
    return [
      'Treat each position as one digit and add l1[i] + l2[i] + carry.',
      'Write digit = sum % 10 to result and update carry = floor(sum / 10).',
      'Continue until both arrays are consumed and carry is 0.',
    ];
  }

  if (fn === 'search') {
    return [
      'Start with left = 0 and right = n - 1.',
      'Check mid; if nums[mid] is target, return mid.',
      'Otherwise discard the half where target cannot exist and repeat.',
    ];
  }

  if (fn === 'searchInsert') {
    return [
      'Run binary search on the sorted array with left and right pointers.',
      'If target is found at mid, return mid immediately.',
      'If not found, loop ends when left passes right; return left as insertion index.',
    ];
  }

  if (fn === 'moveZeroesStable') {
    return [
      'Use read pointer to scan every element and write pointer for next non-zero slot.',
      'When read sees non-zero, swap into write position and increment write.',
      'After full scan, all non-zeros keep relative order at front and zeros stay at end.',
    ];
  }

  if (fn === 'maxSubArrayKadane') {
    return [
      'Define current as best subarray sum ending at current index.',
      'For each value, set current = max(value, current + value).',
      'Track global best across all current values and return it at the end.',
    ];
  }

  if (fn === 'maxProfitSingle') {
    return [
      'Scan prices once while storing minimum price seen so far.',
      'At each day compute candidate profit = price - minPrice.',
      'Keep the maximum candidate profit as the final answer.',
    ];
  }

  if (fn === 'findMin') {
    return [
      'Use binary search on rotated sorted array boundaries.',
      'Compare nums[mid] with nums[right] to decide which half contains minimum.',
      'Stop when left == right; that value is minimum.',
    ];
  }

  if (fn === 'threeSum' || fn === 'threeSumClosest') {
    return [
      'Sort the array first so two-pointer movement has meaning.',
      'Fix one index i, then move left/right pointers to adjust the sum.',
      'Skip duplicates where required so result remains valid and unique.',
    ];
  }

  if (fn === 'lengthOfLongestSubstring') {
    return [
      'Maintain a sliding window [left..right] with no duplicate characters.',
      'If duplicate appears, move left just after previous occurrence.',
      'Track maximum window length during traversal.',
    ];
  }

  if (fn === 'merge') {
    return [
      'Sort intervals by start value.',
      'Compare each interval with the last merged interval.',
      'Merge on overlap; otherwise append as a new interval.',
    ];
  }

  if (fn === 'climbStairs' || fn === 'coinChange') {
    return [
      'Define smaller subproblems first (base states).',
      'Build answer iteratively using already computed states.',
      'Final DP state gives required output.',
    ];
  }

  if (fn === 'canFinish') {
    return [
      'Create indegree count and adjacency list from prerequisites.',
      'Push all indegree-0 courses into queue and process them one by one.',
      'If processed count equals total courses, schedule is possible; else cycle exists.',
    ];
  }

  if (fn === 'numIslands') {
    return [
      'Scan each cell in grid.',
      'When unvisited land is found, run DFS/BFS to mark full connected component.',
      'Count how many such component-starts happen.',
    ];
  }

  if (fn === 'ladderLength' || fn === 'shortestPathBinaryMatrix') {
    return [
      'Model each valid state as a graph node and run BFS.',
      'BFS guarantees first time you reach target is shortest path length.',
      'If target is unreachable, return 0 or -1 as required by problem.',
    ];
  }

  if (fn === 'countPrefixMatches') {
    return [
      'Insert all words into trie while storing prefix counts.',
      'For each query prefix, walk trie edges character by character.',
      'If path exists, return stored count; otherwise return 0.',
    ];
  }

  switch (template.visualType) {
    case 'array-pointer':
      return [
        'Track one or more pointers over the array.',
        'Update pointers based on comparison with target or rule.',
        'Stop when condition is satisfied and return computed answer.',
      ];
    case 'binary-search':
      return [
        'Keep a search range [left, right].',
        'Use middle element to remove half the range each step.',
        'Repeat until answer index/value is identified.',
      ];
    case 'stack':
      return [
        'Push elements that must be resolved later.',
        'Pop when current element resolves pending condition.',
        'Build output from each resolved pop/update action.',
      ];
    case 'window':
      return [
        'Scan left to right while maintaining a valid running window/state.',
        'Adjust left/right boundaries or running minimum/maximum when needed.',
        'Update best answer after each meaningful state change.',
      ];
    case 'intervals':
      return [
        'Sort intervals first for predictable overlap checks.',
        'Compare current interval with last output interval.',
        'Merge or append based on overlap condition.',
      ];
    case 'dp':
      return [
        'Write base cases clearly.',
        'Use recurrence relation to fill next states from previous states.',
        'Read final answer from last required DP state.',
      ];
    case 'graph':
      return [
        'Represent relationships as nodes and edges.',
        'Traverse using BFS/DFS/topological logic depending on requirement.',
        'Use visited/indegree state to avoid repeated or invalid processing.',
      ];
    case 'linked-list':
      return [
        'Move node-by-node with clear pointer updates.',
        'Keep track of carry/state when combining node values.',
        'Return rebuilt list/array once traversal finishes.',
      ];
    case 'queue':
      return [
        'Process operations in arrival order.',
        'Update internal state after each enqueue/dequeue/get/put action.',
        'Collect outputs exactly at required operation points.',
      ];
    case 'tree':
      return [
        'Traverse or build tree/trie according to node relationships.',
        'Use node-level counters/state to answer query or compute result.',
        'Aggregate final answer from traversal outcomes.',
      ];
    default:
      return [
        'Understand the input and output contract first.',
        'Break problem into small repeatable steps.',
        'Run those steps consistently to build final answer.',
      ];
  }
};

const buildDetailedStatement = (item, template) => {
  if (PROBLEM_QUESTION_OVERRIDES[item.title]?.statement) {
    return PROBLEM_QUESTION_OVERRIDES[item.title].statement;
  }

  if (item.title === LONGEST_SUBSTRING_TITLE) {
    return `
<p>Given a string <strong>s</strong>, find the length of the longest substring without repeating characters.</p>
<p>A substring is a contiguous sequence of characters inside the string.</p>
<p>Return the maximum possible length.</p>
`.trim();
  }

  const inputSummary = buildInputSummary(template);
  return `
<p><strong>${escapeHtml(item.title)}</strong></p>
<p>${escapeHtml(inputSummary)}</p>
<p><strong>Task:</strong> ${escapeHtml(template.statement)}</p>
<p>Return the result for the given input according to the problem rules.</p>
`.trim();
};

const buildDetailedDescription = (item, template) => {
  const firstTest = template.tests[0];
  const secondTest = template.tests[1] || template.tests[0];
  const firstWhy = buildExampleReasoning(template, firstTest);
  const secondWhy = buildExampleReasoning(template, secondTest);
  const approachPoints = buildBeginnerApproach(template)
    .map((point) => `<li>${escapeHtml(point)}</li>`)
    .join('');
  return `
<p><strong>Task:</strong> ${escapeHtml(template.statement)}</p>
<p><strong>Input:</strong> ${escapeHtml(pretty(firstTest.input))}</p>
<p><strong>Output:</strong> ${escapeHtml(pretty(firstTest.expected))}</p>
<p><strong>How to think about it (beginner friendly):</strong></p>
<ul>${approachPoints}</ul>
<p><strong>Why this output:</strong> ${escapeHtml(firstWhy)}</p>
<p><strong>One more walkthrough:</strong> For input ${escapeHtml(pretty(secondTest.input))}, the expected output is ${escapeHtml(pretty(secondTest.expected))}.</p>
<p><strong>Reason:</strong> ${escapeHtml(secondWhy)}</p>
`.trim();
};

const buildDetailedConstraints = (item, template) => {
  const qualityHints = [
    `Match function contract: ${template.functionName}(...).`,
    `Return output exactly in the expected format.`,
    `Aim for the best complexity expected for ${item.difficulty.toLowerCase()} interviews.`,
  ];
  return [...template.constraints, ...qualityHints];
};

const buildDetailedExamples = (item, template) => template.tests.map((test, index) => ({
  title: `Example ${index + 1}`,
  input: pretty(test.input),
  output: pretty(test.expected),
  explanation: buildExampleReasoning(template, test),
}));

const buildCanonicalExamples = (item, template) => {
  if (PROBLEM_QUESTION_OVERRIDES[item.title]?.examples) {
    return PROBLEM_QUESTION_OVERRIDES[item.title].examples;
  }

  if (item.title !== LONGEST_SUBSTRING_TITLE) return buildDetailedExamples(item, template);
  return [
    {
      title: 'Example 1',
      input: '["abcabcbb"]',
      output: '3',
      explanation: 'The answer is "abc", with the length of 3.',
    },
    {
      title: 'Example 2',
      input: '["bbbbb"]',
      output: '1',
      explanation: 'The answer is "b", with the length of 1.',
    },
    {
      title: 'Example 3',
      input: '["pwwkew"]',
      output: '3',
      explanation: 'The answer is "wke", with the length of 3. Notice that "pwke" is a subsequence and not a substring.',
    },
  ];
};

const mapItemToProblem = (item, index = 0, resolvedId = null) => {
  const normalizedSource = item.source || 'curated';
  const mappedTemplate = normalizedSource === 'online' ? ONLINE_INTERACTIVE_TEMPLATE_BY_TITLE[item.title] : null;
  const chosenTemplate = normalizedSource === 'online'
    ? mappedTemplate
    : (item.template || mappedTemplate);
  const resolvedTemplate = chosenTemplate ? TEMPLATE_LIBRARY[chosenTemplate] : null;
  const canBeInteractive = Boolean(resolvedTemplate) && normalizedSource !== 'docx-batch';
  const requestedInteractive = normalizedSource === 'online'
    ? canBeInteractive
    : (item.interactive ?? canBeInteractive);
  const isInteractive = Boolean(requestedInteractive && canBeInteractive);
  const template = resolvedTemplate || TEMPLATE_LIBRARY.twoSum;
  const isDocxBatch = normalizedSource === 'docx-batch';
  const detailedStatement = isInteractive
    ? buildDetailedStatement(item, template)
    : (isDocxBatch ? buildDocxStatement(item) : buildMetadataStatement(item));
  const detailedDescription = isInteractive
    ? buildDetailedDescription(item, template)
    : (isDocxBatch ? buildDocxDescription(item) : buildMetadataDescription(item));
  const detailedConstraints = isInteractive
    ? buildDetailedConstraints(item, template)
    : (isDocxBatch ? buildDocxConstraints(item) : buildMetadataConstraints(item));
  const detailedExamples = isInteractive
    ? buildCanonicalExamples(item, template)
    : (isDocxBatch ? buildDocxExamples(item) : []);
  return {
    id: resolvedId || slugify(item.title),
    source: normalizedSource,
    interactive: isInteractive,
    sourceUrl: item.sourceUrl || '',
    title: item.title,
    difficulty: item.difficulty,
    companies: item.companies,
    patterns: item.patterns,
    visualType: inferVisualTypeFromPatterns(item.patterns, template.visualType),
    question: {
      title: item.title,
      statement: detailedStatement,
      description: detailedDescription,
      constraints: detailedConstraints,
      examples: detailedExamples,
      functionName: isInteractive ? template.functionName : '',
      jsStarter: isInteractive ? template.jsStarter : '',
      javaStarter: isInteractive ? template.javaStarter : '',
      tests: isInteractive ? template.tests : [],
    },
    order: index + 1,
  };
};

const combinedSpecs = [...ONLINE_PROBLEM_SPECS, ...BATCH_PROBLEM_SPECS, ...API_QUESTION_SPECS, ...CURATED_PROBLEM_SPECS];

const dedupedByTitle = Array.from(new Map(combinedSpecs.map((item) => [item.title, item])).values());

const slugCounts = new Map();
const mappedProblems = dedupedByTitle.map((item, index) => {
  const base = slugify(item.title);
  const nextCount = (slugCounts.get(base) || 0) + 1;
  slugCounts.set(base, nextCount);
  const uniqueId = nextCount === 1 ? base : `${base}-${nextCount}`;
  return mapItemToProblem(item, index, uniqueId);
});
export const BATCH_PROBLEMS = mappedProblems.filter((problem) => problem.source === 'docx-batch');
export const TOTAL_BATCH_PROBLEM_COUNT = BATCH_PROBLEMS.length;
export const PROBLEMS = mappedProblems.filter((problem) => problem.interactive);
export const ALL_PROBLEMS = PROBLEMS;
export const TOTAL_PROBLEM_COUNT = PROBLEMS.length;

export { PATTERN_STATS, TEMPLATE_LIBRARY, slugify };
